import { IAccountMenu, IMenuItem } from './types';
import { FC, memo, useMemo, useState } from 'react';
import { Box, Stack, Typography, CircularProgress } from '@mui/material';
import { BrandLogoWhite } from '../Logos';
import { isExternal } from '../../utils';
import { Link } from 'react-router-dom';
import { OpenInNew } from '@mui/icons-material';
import { AccountDropdown } from './AccountDropdown';
import { useBrandVersion } from '../../hooks';

export interface ITopBarHeaderMenuProps {
    menuItems: IMenuItem[];
    accountMenu: IAccountMenu;
}

export const TopBarHeaderMenu: FC<ITopBarHeaderMenuProps> = memo(({ menuItems, accountMenu }) => {
    const [isMenuVisible] = useState(true);
    const [loadingItem, setLoadingItem] = useState<string | null>(null);
    const subdomain = window.location.href.split('.')[0].toLowerCase().replace('https://', '');
    const brandVersion = useBrandVersion();
    const isNewBrand = brandVersion === 'newBrand';

    const environmentMessage = useMemo(() => {
        switch (true) {
            case subdomain.indexOf('localhost') > -1:
                return 'localhost';
            case subdomain.indexOf('azutst01') > -1:
            case subdomain.indexOf('hqtst01') > -1:
                return 'QA-01';
            case subdomain.indexOf('azutst02') > -1:
            case subdomain.indexOf('hqtst02') > -1:
                return 'QA-02';
            case subdomain.indexOf('azutst03') > -1:
            case subdomain.indexOf('hqtst03') > -1:
                return 'QA-03';
            case subdomain.indexOf('azustg') > -1:
                return 'Staging';
            case subdomain.indexOf('azups') > -1:
                return 'ProServ';
            default:
                return undefined;
        }
    }, [subdomain]);

    return (
        <header data-testid="top-bar-header-menu">
            <Stack>
                {environmentMessage ? (
                    <Box
                        sx={{
                            bgcolor: 'primary.900',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <Typography sx={{ color: 'error.300', fontWeight: 'bold' }}>{environmentMessage}</Typography>
                    </Box>
                ) : null}
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ height: 72, bgcolor: 'primary.700' }}
                >
                    <Box sx={{ px: 4 }}>
                        <a href="https://transcepta.com/">
                            <BrandLogoWhite />
                        </a>
                    </Box>
                    {isMenuVisible ? (
                        <Stack direction="row" sx={{ height: '100%' }}>
                            {menuItems.map((item) => {
                                const external = isExternal(item.path);
                                const selected = window.location.pathname.includes(item.path);

                                return (
                                    <Link
                                        to={item.path}
                                        key={item.name}
                                        style={{ height: '100%' }}
                                        data-testid={`menu-option-${item.testId}`}
                                        onClick={() => {
                                            setLoadingItem(item.path);

                                            // Clear loading state after a short delay to allow navigation
                                            setTimeout(() => {
                                                setLoadingItem(null);
                                            }, 1000);
                                        }}
                                    >
                                        <Box
                                            className={isNewBrand && selected ? 'header-nav-tab-selected' : undefined}
                                            sx={
                                                isNewBrand
                                                    ? {
                                                          boxSizing: 'border-box',
                                                          mx: 2,
                                                          height: '100%',
                                                          pt: 3.5,
                                                      }
                                                    : selected
                                                    ? {
                                                          boxSizing: 'border-box',
                                                          mx: 2,
                                                          height: '100%',
                                                          borderBottom: 6,
                                                          borderColor: 'primary.400',
                                                          pt: 3.5,
                                                      }
                                                    : {
                                                          mx: 2,
                                                          height: '100%',
                                                          pt: 3.5,
                                                      }
                                            }
                                        >
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Typography
                                                    variant="button"
                                                    color="white"
                                                    noWrap
                                                    sx={{ textTransform: 'capitalize' }}
                                                >
                                                    {item.name}
                                                </Typography>
                                                {external && <OpenInNew sx={{ fontSize: 14 }} />}
                                                {loadingItem === item.path && (
                                                    <CircularProgress size={16} sx={{ color: 'white' }} />
                                                )}
                                            </Stack>
                                        </Box>
                                    </Link>
                                );
                            })}
                        </Stack>
                    ) : null}
                    <Box>
                        <AccountDropdown accountMenu={accountMenu} />
                    </Box>
                </Stack>
            </Stack>
        </header>
    );
});
